import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { grantWaiverBulk, listSubHeadConfigs } from "../../api/feeApi";
import "../Attendance/Attendance.css";
import "../Users/UsersPage.css";

const academicYearApi = createLookupApi("academic-years");
const categoryApi = createLookupApi("student-categories");
const feeHeadApi = createLookupApi("fee-heads");
const feeWaiverApi = createLookupApi("fee-waivers");

/**
 * Direct port of digicampusbd's real `fee-waiver-configuration` ("Fee Waiver
 * Setup") — a bulk, roster-search-driven grant: search students by year +
 * section + group (optional) + category, check many of them, then grant the
 * SAME waiver type + amount to all of them at once, across one or more
 * selected fee sub-heads. Confirmed via the old app's own real template
 * (`fee-waiver-configuration.html`) and `FeeWaiverConfigurationService
 * .saveFeeWaiverConfiguration`.
 *
 * Replaces this page's earlier, single-student-only implementation (the old
 * `FeeAdjustmentsPage`'s "Waiver Grants" tab, still in the codebase but no
 * longer routed here) — that version didn't correspond to any real old-app
 * screen's actual workflow. See MIGRATION.md for the full finding.
 */
export default function FeeWaiverGrantPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  const [students, setStudents] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searching, setSearching] = useState(false);

  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [selectedSubHeadIds, setSelectedSubHeadIds] = useState([]);
  const [feeWaivers, setFeeWaivers] = useState([]);
  const [feeWaiverId, setFeeWaiverId] = useState("");
  const [waiverAmount, setWaiverAmount] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    categoryApi.list().then(setCategories);
    feeHeadApi.list().then(setFeeHeads);
    feeWaiverApi.list().then(setFeeWaivers);
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    setGroupId("");
    setGroups([]);
    if (cc) listGroupConfigurationsByClassId(cc.classId).then(setGroups);
  }, [classConfigId, classConfigs]);

  useEffect(() => {
    if (!feeHeadId) {
      setFeeSubHeads([]);
      return;
    }
    // Reuses the fee sub-head-configuration list already built for FeeConfigPage.
    listSubHeadConfigs(feeHeadId).then((rows) => setFeeSubHeads(rows.map((r) => ({ id: r.feeSubHeadId, name: r.feeSubHeadName }))));
    setSelectedSubHeadIds([]);
  }, [feeHeadId]);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!academicYear || !classConfigId || !categoryId) {
      setError("Please select a year, section, and category.");
      return;
    }
    setSearching(true);
    try {
      const data = await listStudents(academicYear, classConfigId);
      const filtered = data
        .filter((s) => !groupId || String(s.groupId) === String(groupId))
        .filter((s) => String(s.categoryId) === String(categoryId))
        .sort((a, b) => a.studentRoll - b.studentRoll);
      setStudents(filtered);
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load students.");
    } finally {
      setSearching(false);
    }
  };

  const toggleStudent = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelectedIds((prev) => (students && prev.length === students.length ? [] : (students || []).map((s) => s.identificationId)));
  const toggleSubHead = (id) => setSelectedSubHeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleGrant = async () => {
    setError("");
    setSuccess("");
    if (selectedIds.length === 0) {
      setError("Please select at least one student.");
      return;
    }
    if (!feeHeadId || selectedSubHeadIds.length === 0 || !feeWaiverId) {
      setError("Please select a fee head, at least one sub-head, and a waiver type.");
      return;
    }
    try {
      await grantWaiverBulk({
        identificationIds: selectedIds,
        feeHeadId: Number(feeHeadId),
        feeSubHeadIds: selectedSubHeadIds.map(Number),
        feeWaiverId: Number(feeWaiverId),
        waiverAmount: Number(waiverAmount) || 0,
      });
      setSuccess(`Waiver granted to ${selectedIds.length} student(s).`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to grant waiver.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Waiver Setup</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Student Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
            <option value="" disabled>
              Select
            </option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {students && (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={students.length > 0 && selectedIds.length === students.length} onChange={toggleAll} />
                  </th>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Roll</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.identificationId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(s.identificationId)}
                        onChange={() => toggleStudent(s.identificationId)}
                      />
                    </td>
                    <td>{s.customStudentId}</td>
                    <td>{s.studentName}</td>
                    <td>{s.studentRoll}</td>
                  </tr>
                ))}
                {students.length === 0 && (
                  <tr>
                    <td colSpan={4}>No students found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 style={{ marginTop: 24 }}>Grant Waiver to {selectedIds.length} selected student(s)</h3>
          <div className="attendance-filters">
            <label>
              Fee Head
              <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)} required>
                <option value="" disabled>
                  Select
                </option>
                {feeHeads.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Waiver Type
              <select value={feeWaiverId} onChange={(e) => setFeeWaiverId(e.target.value)} required>
                <option value="" disabled>
                  Select
                </option>
                {feeWaivers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Waiver Amount
              <input type="number" value={waiverAmount} onChange={(e) => setWaiverAmount(e.target.value)} required />
            </label>
          </div>
          <div style={{ margin: "8px 0 16px" }}>
            <span className="admit-card-label">Fee Sub-Head(s):</span>{" "}
            {feeSubHeads.map((s) => (
              <label key={s.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
                <input type="checkbox" checked={selectedSubHeadIds.includes(String(s.id))} onChange={() => toggleSubHead(String(s.id))} />
                {s.name}
              </label>
            ))}
            {feeHeadId && feeSubHeads.length === 0 && <span>No sub-heads configured for this fee head.</span>}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleGrant}>
              Grant Waiver
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
